/*
 * <quickview-variants>
 *
 * Self-contained variant picker for the Quick view drawer (sections/api-product-quickview.liquid).
 *
 * The quick view HTML is injected into the modal via innerHTML, which means inline
 * <script> tags never run and the theme's compiled product-form wiring does not take
 * effect. A custom element, however, is upgraded synchronously on injection, so this
 * is the reliable place to make swatch selection actually do something.
 *
 * On option change it updates, purely from a Liquid-rendered variant map:
 *   - the visible product image (shows the selected variant's image, hides the rest)
 *   - the header price / compare-at price / "Save" badge
 *   - the selected option label (e.g. "Choose your colour | Yellow")
 *   - the add-to-cart button variant id, price and availability
 */
(function () {
  if (customElements.get('quickview-variants')) return;

  class QuickviewVariants extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.variants = this.readVariants();
      if (!this.variants || !this.variants.length) return;

      this.addToCartText = this.dataset.addText || 'Add to cart';
      this.soldOutText = this.dataset.soldOutText || 'Sold out';

      this.idInput = this.querySelector('input[name="id"]');
      this.addButton = this.querySelector('[data-add-to-cart]');
      this.buttonPrice = this.querySelector('[data-button-price]');
      this.addText = this.querySelector('[data-add-to-cart-text]');
      this.priceEl = this.querySelector('[data-product-price]');
      this.compareEl = this.querySelector('[data-compare-price]');
      this.priceOff = this.querySelector('[data-price-off]');
      this.priceOffAmount = this.querySelector('[data-price-off-amount]');

      this._onChange = this._onChange.bind(this);
      this.addEventListener('change', this._onChange);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this._onChange);
    }

    _onChange(event) {
      var target = event.target;
      if (target && target.name && target.name.indexOf('options[') === 0) {
        this.onOptionChange();
      }
    }

    readVariants() {
      var el = this.querySelector('[data-quickview-variant-json]');
      if (!el) return null;
      try {
        return JSON.parse(el.textContent);
      } catch (e) {
        return null;
      }
    }

    selectedOptions() {
      var wrappers = this.querySelectorAll('[data-option-position]');
      if (!wrappers.length) return null;
      var opts = [];
      wrappers.forEach(function (wrapper) {
        var pos = parseInt(wrapper.dataset.optionPosition, 10);
        var checked = wrapper.querySelector('input[type="radio"]:checked');
        var select = wrapper.querySelector('select');
        var value = checked ? checked.value : (select ? select.value : null);
        opts[(pos || opts.length + 1) - 1] = value;
      });
      return opts;
    }

    matchVariant(opts) {
      if (!opts) return this.variants[0];
      return this.variants.find(function (v) {
        return (
          v.options.length === opts.length &&
          v.options.every(function (value, i) {
            return value === opts[i];
          })
        );
      });
    }

    onOptionChange() {
      this.updateOptionLabels();
      var variant = this.matchVariant(this.selectedOptions());
      if (!variant) {
        this.setUnavailable();
        return;
      }
      if (this.idInput) this.idInput.value = variant.id;
      this.updateButton(variant);
      this.updatePrice(variant);
      this.updateImage(variant);
    }

    updateOptionLabels() {
      this.querySelectorAll('[data-option-position]').forEach(function (wrapper) {
        var target = wrapper.querySelector('[data-option-value]');
        var checked = wrapper.querySelector('input[type="radio"]:checked');
        var select = wrapper.querySelector('select');
        var value = checked ? checked.value : (select ? select.value : null);
        if (target && value != null) target.textContent = value;
      });
    }

    updateButton(variant) {
      if (this.addButton) this.addButton.disabled = !variant.available;
      if (this.addText) {
        this.addText.textContent = variant.available ? this.addToCartText : this.soldOutText;
      }
      if (this.buttonPrice) {
        this.buttonPrice.innerHTML = variant.button_price;
        this.buttonPrice.style.display = variant.available ? '' : 'none';
      }
    }

    setUnavailable() {
      if (this.addButton) this.addButton.disabled = true;
      if (this.addText) this.addText.textContent = this.soldOutText;
    }

    updatePrice(variant) {
      if (this.priceEl) {
        this.priceEl.innerHTML = variant.price;
        this.priceEl.classList.toggle('product__price--sale', variant.on_sale);
      }
      if (this.compareEl) {
        if (variant.on_sale) {
          this.compareEl.innerHTML = variant.compare_at;
          this.compareEl.classList.remove('hide');
        } else {
          this.compareEl.classList.add('hide');
        }
      }
      if (this.priceOff) {
        if (variant.on_sale) {
          if (this.priceOffAmount) this.priceOffAmount.innerHTML = variant.save;
          this.priceOff.classList.remove('hide');
        } else {
          this.priceOff.classList.add('hide');
        }
      }
    }

    updateImage(variant) {
      if (!variant.featured_media_id) return;
      var id = String(variant.featured_media_id);
      var slides = this.querySelectorAll('[data-media-slide]');
      if (!slides.length) return;
      var matched = null;
      slides.forEach(function (slide) {
        var isMatch = slide.getAttribute('data-media-id') === id;
        slide.style.display = isMatch ? '' : 'none';
        if (isMatch) matched = slide;
      });
      if (!matched) return;
      // Make sure the now-visible image is loaded (in case it was lazy).
      var img = matched.querySelector('img');
      if (img) {
        if (img.dataset.src && !img.getAttribute('src')) img.setAttribute('src', img.dataset.src);
        if (img.dataset.srcset && !img.getAttribute('srcset')) img.setAttribute('srcset', img.dataset.srcset);
        img.loading = 'eager';
        img.style.opacity = '1';
      }
      var scroller = this.scrollParent(matched);
      if (scroller) scroller.scrollTop = 0;
    }

    scrollParent(el) {
      var node = el.parentElement;
      while (node) {
        var overflowY = window.getComputedStyle(node).overflowY;
        if (
          (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay') &&
          node.scrollHeight > node.clientHeight
        ) {
          return node;
        }
        node = node.parentElement;
      }
      return null;
    }
  }

  customElements.define('quickview-variants', QuickviewVariants);
})();
